import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import DraftModal from './DraftModal';
import Button from 'react-bootstrap/Button';
import AddNewTemplateFormik from '../../forms/AddNewTemplateFormContainer';
import { Dropdown, DropdownBody } from '../../components/dropdown';
import { mutations, queriesV2 } from '../../api';
import { useNotifications } from '../../notifications';
import { SuccessMessages } from '../../success-messages';
import { paths } from '../../routing';
import { TemplateType } from '../../consts/forms-types';
import { getTemplateType, DocumentTemplateType } from '../../shared/templateType';
import { ReactComponent as DotsIconSvg } from '../../shared/icons/dotsRoundIcon.svg';

const DotsIcon = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>((props, ref) => (
  <span {...props} ref={ref} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><DotsIconSvg /></span>
));
import { TitleFormattedMessage } from '../../components/titleFormattedMessage';
import MoveTemplateModal from './components/MoveTemplateModal';
import CloneTemplateModal from './components/CloneTemplateModal';
import ShareToWorkspacesModal from './components/ShareToWorkspacesModal';
import WordExportToggleModal from './WordExportToggleModal';
import RestrictedEditingToggleModal from './RestrictedEditingToggleModal';
import PropdocsModal from '../../components/modal/PropdocsModal';
import { useDropdownControl } from '../../contexts/DropdownContext';
import styles from './styles.module.scss';
import { TemplateStatus, TemplateActionStatus } from './templateConstants';

type TemplateControlsProps = {
  status: string;
  editPermission: boolean;
  managePermission?: boolean; // New prop for manage_templates permission
  selectedTemplateData: any;
  teamId: string;
  onSuccess: () => void;
  templateType?: TemplateType;
};

type DropdownActionsType = {
  text: string;
  onClick: () => void;
  isAvailable: boolean;
};

// Re-export for backward compatibility
export { TemplateStatus };

const Controls = ({
  status,
  editPermission,
  managePermission,
  selectedTemplateData,
  teamId,
  onSuccess,
  templateType,
}: TemplateControlsProps) => {
  const { formatMessage } = useIntl();
  const dropdownId = useMemo(
    () => `template-dropdown-${selectedTemplateData?.id || Math.random()}`,
    [selectedTemplateData?.id]
  );
  const { isOpen, onToggle } = useDropdownControl(dropdownId);
  const [isDraftModalOpen, setDraftModalOpen] = useState(false);
  const [isActivateModalOpen, setActivateModalOpen] = useState(false);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showWordExportModal, setShowWordExportModal] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentTemplateType, setCurrentTemplateType] = useState<TemplateType>(TemplateType.DOCUMENT);
  const navigate = useNavigate();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [updateDocumentTemplate] = useMutation(mutations.updateDocumentTemplate as any);
  const [updateTemplateEditorConfiguration] = useMutation(mutations.updateTemplateEditorConfiguration);
  const isOptionAvailable = editPermission;
  const isManageAvailable = managePermission ?? editPermission; // Use managePermission if provided, fallback to editPermission for backward compatibility
  const isActive = status === TemplateStatus.ACTIVE;
  const hasWordExportDisabled = selectedTemplateData?.editorConfiguration?.disable_word_export || false;
  const isRestrictedEditing = selectedTemplateData?.editorConfiguration?.restricted_editing || false;
  // Restricted editing is a docx-native feature (the CKE path handles it differently).
  const isDocxNativeTemplate =
    selectedTemplateData?.editorConfiguration?.editor_engine === 'docx_native';

  const navigateToEditor = useCallback(
    (templateId: string, teamId: string) => {
      const dealUrl = paths.templates.document.replace(':teamId', teamId).replace(':templateId', templateId);
      navigate(dealUrl);
    },
    [navigate]
  );

  useEffect(() => {
    !!templateType && setCurrentTemplateType(templateType);
  }, [templateType]);

  const onUpdateDraft = async () => {
    const deactivateMessage =
      currentTemplateType === TemplateType.CLAUSE
        ? SuccessMessages.deactivateClause
        : SuccessMessages.deactivateTemplate;
    const activateMessage =
      currentTemplateType === TemplateType.CLAUSE ? SuccessMessages.activateClause : SuccessMessages.activateTemplate;
    const successMessage = isActive ? deactivateMessage : activateMessage;
    try {
      await updateDocumentTemplate({
        variables: { documentTemplateId: selectedTemplateData.id, hidden: isActive },
        refetchQueries: [
          {
            query: queriesV2.getDocumentTemplates,
            variables: {
              teamId,
              includeContent: true,
            },
          },
        ],
      });
      notifySuccess({ message: successMessage });
      setDraftModalOpen(false);
      onSuccess();
    } catch (error) {
      notifyError({ message: error });
      return false;
    }
  };

  // Remove old onTemplateClone callback - now handled by CloneTemplateModal

  const onToggleWordExport = useCallback(async () => {
    try {
      await updateTemplateEditorConfiguration({
        variables: {
          templateId: selectedTemplateData.id,
          configuration: {
            disableWordExport: !hasWordExportDisabled,
          },
        },
        refetchQueries: [
          {
            query: queriesV2.getDocumentTemplates,
            variables: {
              teamId,
              includeContent: true,
            },
          },
        ],
      });

      const message = hasWordExportDisabled
        ? 'Word export has been enabled for this template'
        : 'Word export has been disabled for this template';

      notifySuccess({ message });
      setShowWordExportModal(false);
      onSuccess();
    } catch (error) {
      notifyError({ message: 'Failed to update template settings' });
      return false;
    }
  }, [
    updateTemplateEditorConfiguration,
    selectedTemplateData,
    hasWordExportDisabled,
    notifySuccess,
    notifyError,
    onSuccess,
    teamId,
  ]);

  // Toggle restricted editing on/off. Turning it OFF just STOPS ENFORCING — the
  // marked editable regions stay in the body (dormant, reversible), so this is a
  // safe single-confirm, not a destructive removal.
  const onToggleRestricted = useCallback(async () => {
    try {
      await updateTemplateEditorConfiguration({
        variables: {
          templateId: selectedTemplateData.id,
          configuration: {
            restrictedEditing: !isRestrictedEditing,
          },
        },
        refetchQueries: [
          {
            query: queriesV2.getDocumentTemplates,
            variables: { teamId, includeContent: true },
          },
        ],
      });

      notifySuccess({
        message: isRestrictedEditing
          ? 'This template is now fully editable'
          : 'Restricted editing has been enabled for this template',
      });
      setShowRestrictedModal(false);
      onSuccess();
    } catch (error) {
      notifyError({ message: 'Failed to update template settings' });
      return false;
    }
  }, [
    updateTemplateEditorConfiguration,
    selectedTemplateData,
    isRestrictedEditing,
    notifySuccess,
    notifyError,
    onSuccess,
    teamId,
  ]);

  const allDropDownActions = [
    {
      text: TemplateActionStatus.view,
      onClick: () => {
        navigateToEditor(selectedTemplateData.id, teamId);
      },
      isAvailable: true,
    },
    {
      text: TemplateActionStatus.move,
      onClick: () => {
        setShowMoveModal(true);
      },
      isAvailable: isManageAvailable,
    },
    {
      text: TemplateActionStatus.clone,
      onClick: () => {
        setShowCloneModal(true);
      },
      isAvailable: isManageAvailable && templateType !== TemplateType.UPLOADED,
    },
    {
      text: selectedTemplateData?.sharedToAffiliatedWorkspaces
        ? TemplateActionStatus.unshare
        : TemplateActionStatus.share,
      onClick: () => {
        setShowShareModal(true);
      },
      isAvailable: isManageAvailable && !selectedTemplateData?.sharedFromTeam, // Only show in source workspace
    },
    {
      text: hasWordExportDisabled ? 'Enable Word Export' : 'Disable Word Export',
      onClick: () => {
        setShowWordExportModal(true);
      },
      isAvailable: isManageAvailable && templateType === TemplateType.DOCUMENT && getTemplateType(selectedTemplateData) !== DocumentTemplateType.PDF,
    },
    {
      text: isRestrictedEditing ? 'Make Fully Editable' : 'Make Restricted',
      onClick: () => {
        setShowRestrictedModal(true);
      },
      // Restricted editing is a docx-native feature; only offer it there.
      isAvailable:
        isManageAvailable &&
        isDocxNativeTemplate &&
        templateType === TemplateType.DOCUMENT &&
        getTemplateType(selectedTemplateData) !== DocumentTemplateType.PDF,
    },
    {
      text: isActive ? 'Deactivate' : 'Activate',
      onClick: isActive ? () => setDraftModalOpen(true) : () => setActivateModalOpen(true),
      isAvailable: isManageAvailable,
    },
  ];

  const dropDownActions = (actions: DropdownActionsType[]) => {
    return actions
      .filter((action) => action.isAvailable)
      .map((action, index) => (
        <Dropdown.Item as={Button} variant="link" onClick={action.onClick} key={index}>
          {action.text}
        </Dropdown.Item>
      ));
  };

  const templateIsUpdated = () => {
    onSuccess();
    setShowEditTemplate(false);
  };

  return (
    <>
      <Dropdown show={isOpen} onToggle={onToggle}>
        <Dropdown.Toggle id="dropdown-deal-actions" className={styles.dottedDropdown} as={DotsIcon}>
          <FormattedMessage id="deals.actions" defaultMessage={`Actions`} />
        </Dropdown.Toggle>
        <Dropdown.Menu align="right">
          <DropdownBody>{dropDownActions(allDropDownActions)}</DropdownBody>
        </Dropdown.Menu>
      </Dropdown>
      <DraftModal
        isOpen={isDraftModalOpen}
        onClose={() => setDraftModalOpen(false)}
        onUpdateDraft={onUpdateDraft}
        templateType={templateType}
      />
      <PropdocsModal
        show={isActivateModalOpen}
        setShow={() => setActivateModalOpen(false)}
        size="md"
        title={
          <FormattedMessage
            id="title.activate-template"
            defaultMessage={`Activate ${currentTemplateType === TemplateType.CLAUSE ? 'Clause' : 'Template'}`}
          />
        }
      >
        <>
          <div>
            <p>
              <FormattedMessage
                id="phrase.activate-template"
                defaultMessage={
                  currentTemplateType === TemplateType.CLAUSE
                    ? 'Activating this clause will make it available for insertion into documents.'
                    : 'Activating this template will make it available for creating new documents.'
                }
              />
            </p>
            <p>
              <FormattedMessage
                id="phrase.activate-template-visibility"
                defaultMessage="Users will see this {type} in their {type} list and be able to use it."
                values={{ type: currentTemplateType === TemplateType.CLAUSE ? 'clause' : 'template' }}
              />
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '1rem 0 1rem 0' }}>
            <Button variant="link" className="text-secondary" onClick={() => setActivateModalOpen(false)}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onUpdateDraft();
                setActivateModalOpen(false);
              }}
            >
              <TitleFormattedMessage id="cta.activate" defaultMessage="Activate" />
            </Button>
          </div>
        </>
      </PropdocsModal>
      <Modal show={showEditTemplate} onHide={() => setShowEditTemplate(false)} size="sm">
        <Modal.Header closeButton closeLabel={formatMessage({ id: 'cta.close', defaultMessage: 'Close' })}>
          <Modal.Title>
            <header>
              <FormattedMessage id="title.edit-template" defaultMessage="Edit Template" />
            </header>
          </Modal.Title>
        </Modal.Header>
        <AddNewTemplateFormik
          bodyContainer={Modal.Body}
          actionsContainer={Modal.Footer}
          renderSecondaryAction={() => (
            <Button variant="link" className="text-secondary" onClick={() => setShowEditTemplate(false)}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
          )}
          existingTemplateFields={selectedTemplateData}
          templateFormEditMode={true}
          onSuccess={templateIsUpdated}
        />
      </Modal>
      <MoveTemplateModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        templateId={selectedTemplateData?.id || ''}
        templateName={selectedTemplateData?.name || ''}
        teamId={teamId}
        onSuccess={onSuccess}
      />
      <WordExportToggleModal
        isOpen={showWordExportModal}
        onClose={() => setShowWordExportModal(false)}
        onConfirm={onToggleWordExport}
        isDisabled={hasWordExportDisabled}
        templateName={selectedTemplateData?.name || ''}
      />
      <RestrictedEditingToggleModal
        isOpen={showRestrictedModal}
        onClose={() => setShowRestrictedModal(false)}
        onConfirm={onToggleRestricted}
        isRestricted={isRestrictedEditing}
        templateName={selectedTemplateData?.name || ''}
      />
      <ShareToWorkspacesModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        templateId={selectedTemplateData?.id || ''}
        templateName={selectedTemplateData?.name || ''}
        teamId={teamId}
        isShared={selectedTemplateData?.sharedToAffiliatedWorkspaces || false}
        onSuccess={onSuccess}
      />
      <CloneTemplateModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        templateId={selectedTemplateData?.id || ''}
        templateName={selectedTemplateData?.name || ''}
        templateType={templateType === TemplateType.CLAUSE ? 'clause' : 'template'}
        sourceTeamId={teamId}
        sourceIsRestricted={isRestrictedEditing}
        sourceIsDocxNative={isDocxNativeTemplate}
        onSuccess={onSuccess}
      />
    </>
  );
};

export default Controls;
